'use client';

import { useTranslations, useLocale } from 'next-intl';
import { Button } from '@/components/ui';
import { Link } from '@/components/ui';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useRegistrationStatus } from '../libs/useRegistrationStatus';

interface EventRegisterBottomBarProps {
  event: {
    id: string;
    slug: string;
    currency?: string | null;
    registration_closes?: string | null;
  };
  /** Lowest real division price (see `minRegistrationPrice`); null hides the line. */
  priceFrom: number | null;
}

/**
 * Sticky register CTA on small screens. The bar itself takes the page
 * background (iOS Safari tints its toolbar from the bottom of the page, so a
 * full-bleed primary bar turned the whole browser chrome yellow) and the CTA
 * is a pill inside it, with the starting price and close date alongside.
 * `env(safe-area-inset-bottom)` is live because the root viewport sets
 * `viewportFit: 'cover'`.
 */
export function EventRegisterBottomBar({ event, priceFrom }: EventRegisterBottomBarProps) {
  const t = useTranslations('Event');
  const locale = useLocale();
  // Shared with EventRegistrationClient — one session check + one status GET
  // per page view for both consumers. The bar renders nothing until the state
  // is known, so a registered player never sees a flash of the wrong label.
  const { checked, isRegistered, canAddMore } = useRegistrationStatus(event.id);

  if (!checked) return null;
  if (isRegistered && !canAddMore) return null;

  const label = isRegistered
    ? t('registration.alreadyRegistered.addDivision')
    : t('registration.registerButton');
  const priceLabel =
    priceFrom != null && event.currency ? formatCurrency(locale, event.currency, priceFrom) : null;

  return (
    <div
      className="bg-background/90 border-border fixed right-0 bottom-0 left-0 z-40 border-t backdrop-blur md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      role="banner"
      aria-label={label}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <div className="text-muted-foreground min-w-0 flex-1 text-xs leading-snug">
          {priceLabel && (
            <div className="text-foreground truncate text-sm font-semibold">
              {t('registration.registerBar.from', { price: priceLabel })}
            </div>
          )}
          {event.registration_closes && (
            <div className="truncate">
              {t.rich('registration.registerBar.closes', {
                date: () => (
                  <LocalDateTime
                    iso={event.registration_closes as string}
                    locale={locale}
                    format={formatDate}
                  />
                ),
              })}
            </div>
          )}
        </div>
        <Link
          href={`/${locale}/event/${event.id}/${event.slug}/register`}
          className="block shrink-0"
        >
          <Button size="md" className="h-11 rounded-xl px-5 text-sm font-semibold">
            {label}
            <ArrowRightIcon className="ml-1.5 h-4 w-4 shrink-0" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
